import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Button } from '../components/Button';
import { KPICard } from '../components/KPICard';
import { FilterBar, FilterField } from '../components/FilterBar';
import { SyncButton } from '../components/SyncButton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Cell } from 'recharts';
import { Trophy, TrendingUp, Activity, Target, Filter, AlertTriangle, Map, ArrowRight, BarChart2 } from 'lucide-react';
import { KPI_NASIONAL, KPI_BINDA, TOP_10_BINDA, BOTTOM_10_BINDA, TREN_NASIONAL, GRADE_DISTRIBUTION, getNationalInsights, getKpiAlerts, scoreColor, KPI_FILTER_OPTIONS } from '../data/kpiIntelligence';
export default function KPI() {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const [trendPeriod, setTrendPeriod] = useState<'harian' | 'mingguan' | 'bulanan' | 'tahunan'>('bulanan');
  const handleFilterChange = (key: string, value: any) => {
    setFilterValues(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleResetFilters = () => setFilterValues({});
  const filterFields: FilterField[] = [{
    key: 'tahun',
    label: 'Tahun',
    type: 'select',
    options: KPI_FILTER_OPTIONS.tahun.map(t => ({
      label: t,
      value: t
    }))
  }, {
    key: 'triwulan',
    label: 'Triwulan',
    type: 'select',
    options: KPI_FILTER_OPTIONS.triwulan.map(t => ({
      label: t,
      value: t
    }))
  }, {
    key: 'bulan',
    label: 'Bulan',
    type: 'select',
    options: KPI_FILTER_OPTIONS.bulan.map(b => ({
      label: b,
      value: b
    }))
  }, {
    key: 'grade',
    label: 'Grade KPI',
    type: 'select',
    options: KPI_FILTER_OPTIONS.grade.map(g => ({
      label: `Grade ${g}`,
      value: g
    }))
  }];
  const exportColumns = [{
    key: 'kode',
    label: 'Kode BINDA'
  }, {
    key: 'binda',
    label: 'Nama BINDA'
  }, {
    key: 'score',
    label: 'Skor KPI'
  }, {
    key: 'grade',
    label: 'Grade'
  }, {
    key: 'trend',
    label: 'Tren (%)'
  }, {
    key: 'laporanTotal',
    label: 'Total Laporan'
  }];
  const insights = getNationalInsights();
  const alerts = getKpiAlerts();
  return <div className="space-y-6 animate-in fade-in duration-500 max-w-7xl mx-auto pb-12" data-id="element-1442">
      <div className="flex justify-between items-end border-b border-border/50 pb-4" data-id="element-1443">
        <div data-id="element-1444">
          <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground" data-id="element-1445">
            DENS CAKRA KPI ENGINE
          </h1>
          <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-1446">
            National Intelligence Performance Management System
          </p>
        </div>
        <div className="flex gap-3 items-center" data-id="element-1447">
          <Button variant="outline" size="sm" onClick={() => navigate('/kpi/leaderboard')} className="h-8 border-primary/50 text-primary hover:bg-primary/20 font-mono text-xs" data-id="element-1448">
            <Trophy className="w-4 h-4 mr-2" data-id="element-1449" />
            Leaderboard
          </Button>
          <Button variant={showFilters ? 'default' : 'outline'} size="sm" onClick={() => setShowFilters(!showFilters)} className={`h-8 border-border/50 font-mono text-xs ${showFilters ? 'bg-primary text-primary-foreground' : 'bg-background/50'}`} data-id="element-1450">
            <Filter className="w-4 h-4 mr-2" data-id="element-1451" />
            Filter
          </Button>
          <SyncButton data-id="element-1452" />
        </div>
      </div>

      {showFilters && <FilterBar fields={filterFields} values={filterValues} onChange={handleFilterChange} onApply={() => {}} onReset={handleResetFilters} exportData={KPI_BINDA} exportColumns={exportColumns} exportFilename="KPI_Nasional_Executive" data-id="element-1453" />}

      {/* KPI Nasional Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1454">
        <KPICard title="Skor KPI Nasional" value={KPI_NASIONAL.score} icon={Target} colorVar="var(--primary)" trend={{
        value: `+${KPI_NASIONAL.trend}%`,
        label: 'vs Bulan Lalu',
        isPositive: true
      }} data-id="element-1455" />
        <KPICard title="Grade Nasional" value={`Grade ${KPI_NASIONAL.grade}`} icon={Trophy} colorVar="var(--status-active)" progress={{
        value: KPI_NASIONAL.score,
        label: 'Pencapaian',
        target: '100'
      }} data-id="element-1456" />
        <KPICard title="Total Personel Aktif" value={KPI_NASIONAL.totalPersonel.toLocaleString('id-ID')} icon={Activity} colorVar="var(--status-info)" data-id="element-1457" />
        <KPICard title="Total BINDA" value={KPI_NASIONAL.totalBinda} icon={Map} colorVar="var(--status-standby)" data-id="element-1458" />
      </div>

      {/* Analytics & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1459">
        <Card className="glass-panel border-border/50 lg:col-span-2" data-id="element-1460">
          <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-1461">
            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-1462">
              <BarChart2 className="w-4 h-4 text-primary" data-id="element-1463" /> Analisis Otomatis
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3" data-id="element-1464">
            {insights.map(ins => <div key={ins.id} className={`p-3 rounded border text-sm font-mono ${ins.tone === 'positif' ? 'bg-green-500/10 border-green-500/30 text-green-400' : ins.tone === 'negatif' ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-blue-500/10 border-blue-500/30 text-blue-400'}`} data-id="element-1465">
                {ins.text}
              </div>)}
          </CardContent>
        </Card>

        <Card className="glass-panel border-destructive/30" data-id="element-1466">
          <CardHeader className="border-b border-destructive/30 bg-destructive/10" data-id="element-1467">
            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2 text-destructive" data-id="element-1468">
              <AlertTriangle className="w-4 h-4" data-id="element-1469" /> KPI Alerts
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0" data-id="element-1470">
            <div className="max-h-[250px] overflow-y-auto" data-id="element-1471">
              {alerts.map(al => <div key={al.id} className="p-3 border-b border-border/30 hover:bg-white/5 cursor-pointer transition-colors" onClick={() => al.kode && navigate(`/kpi/binda/${al.kode}`)} data-id="element-1472">
                  <div className="flex justify-between items-start mb-1" data-id="element-1473">
                    <span className="text-xs font-bold text-destructive" data-id="element-1474">
                      {al.jenis}
                    </span>
                    <span className="text-[10px] font-mono text-muted-foreground" data-id="element-1475">
                      {al.target}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground" data-id="element-1476">{al.detail}</p>
                </div>)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1477">
        <Card className="glass-panel border-border/50" data-id="element-1478">
          <CardHeader className="border-b border-border/50 bg-card/50 flex flex-row items-center justify-between" data-id="element-1479">
            <CardTitle className="text-sm font-mono uppercase tracking-widest" data-id="element-1480">
              Tren Kinerja Nasional
            </CardTitle>
            <div className="flex gap-2" data-id="element-1481">
              {(['harian', 'mingguan', 'bulanan', 'tahunan'] as const).map(p => <button key={p} onClick={() => setTrendPeriod(p)} className={`text-[10px] font-mono px-2 py-1 rounded transition-colors ${trendPeriod === p ? 'bg-primary text-primary-foreground' : 'bg-background/50 text-muted-foreground hover:text-foreground'}`} data-id="element-1482">
                    {p.toUpperCase()}
                  </button>)}
            </div>
          </CardHeader>
          <CardContent className="p-4 h-[300px]" data-id="element-1483">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1484">
              <LineChart data={TREN_NASIONAL[trendPeriod]} margin={{
              top: 5,
              right: 30,
              left: 0,
              bottom: 5
            }} data-id="element-1485">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} data-id="element-1486" />
                <XAxis dataKey="label" stroke="rgba(255,255,255,0.5)" tick={{
                fontSize: 10,
                fontFamily: 'monospace'
              }} data-id="element-1487" />
                <YAxis domain={['auto', 'auto']} stroke="rgba(255,255,255,0.5)" tick={{
                fontSize: 10,
                fontFamily: 'monospace'
              }} data-id="element-1488" />
                <Tooltip contentStyle={{
                backgroundColor: 'rgba(15, 18, 25, 0.9)',
                borderColor: 'rgba(255,255,255,0.1)',
                borderRadius: '4px'
              }} itemStyle={{
                color: 'var(--primary)',
                fontFamily: 'monospace'
              }} data-id="element-1489" />
                <Line type="monotone" dataKey="score" name="Skor" stroke="var(--primary)" strokeWidth={2} dot={{
                r: 4,
                fill: 'var(--primary)'
              }} data-id="element-1490" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="glass-panel border-border/50" data-id="element-1491">
          <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-1492">
            <CardTitle className="text-sm font-mono uppercase tracking-widest" data-id="element-1493">
              Distribusi Grade BINDA
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 h-[300px]" data-id="element-1494">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1495">
              <BarChart data={GRADE_DISTRIBUTION} margin={{
              top: 20,
              right: 30,
              left: 0,
              bottom: 5
            }} data-id="element-1496">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} data-id="element-1497" />
                <XAxis dataKey="grade" stroke="rgba(255,255,255,0.5)" tick={{
                fontSize: 12,
                fontFamily: 'monospace'
              }} data-id="element-1498" />
                <YAxis stroke="rgba(255,255,255,0.5)" tick={{
                fontSize: 10,
                fontFamily: 'monospace'
              }} data-id="element-1499" />
                <Tooltip cursor={{
                fill: 'rgba(255,255,255,0.05)'
              }} contentStyle={{
                backgroundColor: 'rgba(15, 18, 25, 0.9)',
                borderColor: 'rgba(255,255,255,0.1)',
                borderRadius: '4px'
              }} data-id="element-1500" />
                <Bar dataKey="jumlah" name="Jumlah BINDA" radius={[4, 4, 0, 0]} data-id="element-1501">
                  {GRADE_DISTRIBUTION.map((entry, index) => {
                  const colors = {
                    A: 'var(--status-active)',
                    B: 'var(--status-info)',
                    C: 'var(--status-standby)',
                    D: 'var(--destructive)'
                  };
                  return <Cell key={`cell-${index}`} fill={colors[entry.grade]} data-id="element-1502" />;
                })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Rankings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1503">
        <Card className="glass-panel border-border/50" data-id="element-1504">
          <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-1505">
            <CardTitle className="text-sm font-mono uppercase tracking-widest text-primary" data-id="element-1506">
              Top 10 BINDA
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0" data-id="element-1507">
            <div className="divide-y divide-border/30" data-id="element-1508">
              {TOP_10_BINDA.map((b, i) => <div key={b.kode} className="flex items-center justify-between p-3 hover:bg-white/5 cursor-pointer transition-colors" onClick={() => navigate(`/kpi/binda/${b.kode}`)} data-id="element-1509">
                  <div className="flex items-center gap-3" data-id="element-1510">
                    <span className="text-muted-foreground font-mono w-4 text-right" data-id="element-1511">
                      {i + 1}
                    </span>
                    <div data-id="element-1512">
                      <div className="font-bold text-sm" data-id="element-1513">{b.binda}</div>
                      <div className="text-[10px] font-mono text-muted-foreground" data-id="element-1514">
                        {b.laporanTotal} Laporan
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-1515">
                    <div className="text-right" data-id="element-1516">
                      <div className="font-mono font-bold" style={{
                    color: scoreColor(b.score)
                  }} data-id="element-1517">
                        {b.score}
                      </div>
                      <div className={`text-[10px] font-mono ${b.trend >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-1518">
                        {b.trend >= 0 ? '+' : ''}
                        {b.trend}%
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" data-id="element-1519" />
                  </div>
                </div>)}
            </div>
          </CardContent>
        </Card>

        <Card className="glass-panel border-border/50" data-id="element-1520">
          <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-1521">
            <CardTitle className="text-sm font-mono uppercase tracking-widest text-destructive" data-id="element-1522">
              Bottom 10 BINDA (Perlu Perhatian)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0" data-id="element-1523">
            <div className="divide-y divide-border/30" data-id="element-1524">
              {BOTTOM_10_BINDA.map((b, i) => <div key={b.kode} className="flex items-center justify-between p-3 hover:bg-white/5 cursor-pointer transition-colors" onClick={() => navigate(`/kpi/binda/${b.kode}`)} data-id="element-1525">
                  <div className="flex items-center gap-3" data-id="element-1526">
                    <span className="text-muted-foreground font-mono w-4 text-right" data-id="element-1527">
                      {KPI_BINDA.length - 9 + i}
                    </span>
                    <div data-id="element-1528">
                      <div className="font-bold text-sm" data-id="element-1529">{b.binda}</div>
                      <div className="text-[10px] font-mono text-muted-foreground" data-id="element-1530">
                        {b.laporanTotal} Laporan
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-1531">
                    <div className="text-right" data-id="element-1532">
                      <div className="font-mono font-bold" style={{
                    color: scoreColor(b.score)
                  }} data-id="element-1533">
                        {b.score}
                      </div>
                      <div className={`text-[10px] font-mono ${b.trend >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-1534">
                        {b.trend >= 0 ? '+' : ''}
                        {b.trend}%
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" data-id="element-1535" />
                  </div>
                </div>)}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}